/**
 * Vuex 状态管理模块
 * 
 * 管理全局应用状态，主要包括用户会话信息（登录状态、角色、用户详情）。
 * 所有组件共享的认证状态通过此模块统一管理，确保状态一致性。
 */
import Vue from "vue";
import Vuex from "vuex";

// 引入认证相关的 API 接口
import { getMe, logout } from "@/api/auth";

// 安装 Vuex 插件
Vue.use(Vuex);

/**
 * 创建 Vuex Store 实例
 * 
 * 包含：
 * - state: 状态定义
 * - getters: 派生状态（计算属性）
 * - mutations: 同步状态修改
 * - actions: 异步状态修改
 */
export default new Vuex.Store({
  /**
   * 状态定义
   * 
   * @property {boolean} loaded - 会话状态是否已加载（防止重复初始化）
   * @property {boolean} authenticated - 当前用户是否已认证（登录）
   * @property {string} role - 用户角色：user（用户）、collector（采集者）、admin（管理员）
   * @property {Object|null} user - 用户详细信息（登录后填充）
   * @property {Object|null} collector - 采集者详细信息（登录后填充）
   * @property {Object|null} admin - 管理员详细信息（登录后填充）
   */
  state: {
    // 会话状态是否已加载，用于路由守卫判断是否需要初始化
    loaded: false,
    // 是否已认证（登录）
    authenticated: false,
    // 用户角色：user / collector / admin
    role: "",
    // 用户信息（登录后填充）
    user: null,
    // 采集者信息（登录后填充）
    collector: null,
    // 管理员信息（登录后填充）
    admin: null,
    // 管理员模拟登录信息（管理员进入用户/采集者界面时使用）
    impersonation: null
  },
  /**
   * Getters：派生状态（类似计算属性）
   * 
   * 通过已有状态计算新的状态值，支持在多个组件中复用。
   */
  getters: {
    /**
     * 获取当前用户的显示名称
     * 
     * 根据用户角色返回对应的账户名，用于页面顶部导航栏显示。
     * 
     * @param {Object} state - 当前状态
     * @returns {string} 显示名称（账户名），未登录返回空字符串
     */
    displayName(state) {
      if (state.role === "user" && state.user) return state.user.account;
      if (state.role === "collector" && state.collector) return state.collector.account;
      if (state.role === "admin" && state.admin) return state.admin.account;
      return "";
    }
  },
  /**
   * Mutations：同步状态修改
   * 
   * 必须是同步函数，直接修改 state。通过 commit 调用。
   */
  mutations: {
    /**
     * 设置会话状态
     * 
     * 更新认证状态和用户信息，这是唯一修改会话状态的方式。
     * 
     * @param {Object} state - 当前状态
     * @param {Object} payload - 会话数据
     * @param {boolean} payload.authenticated - 是否已认证
     * @param {string} [payload.role] - 用户角色
     * @param {Object} [payload.user] - 用户信息
     * @param {Object} [payload.collector] - 采集者信息
     * @param {Object} [payload.admin] - 管理员信息
     */
    SET_SESSION(state, payload) {
      // 标记会话状态已加载
      state.loaded = true;
      // 设置认证状态（强制转换为布尔值）
      state.authenticated = Boolean(payload.authenticated);
      // 设置角色（默认为空字符串）
      state.role = payload.role || "";
      // 设置用户信息（默认为 null）
      state.user = payload.user || null;
      // 设置采集者信息（默认为 null）
      state.collector = payload.collector || null;
      // 设置管理员信息（默认为 null）
      state.admin = payload.admin || null;
      // 清除模拟登录状态
      state.impersonation = null;
    },
    /**
     * 设置管理员模拟登录状态
     * 
     * 当管理员进入用户或采集者界面时使用。
     * 
     * @param {Object} state - 当前状态
     * @param {Object} payload - 模拟登录数据
     * @param {string} payload.type - 模拟类型：user 或 collector
     * @param {Object} payload.account - 被模拟的账户信息
     */
    SET_IMPERSONATION(state, payload) {
      state.impersonation = payload || null;
    }
  },
  /**
   * Actions：异步状态修改
   * 
   * 处理异步操作，完成后通过 commit 调用 mutation 修改状态。
   * 通过 dispatch 调用。
   */
  actions: {
    /**
     * 加载会话状态
     * 
     * 从服务端获取当前登录用户信息，初始化会话状态。
     * 通常在应用启动时和路由守卫中调用。
     * 
     * @param {Object} context - 上下文对象，包含 commit、state 等
     * @returns {Promise<Object>} 会话信息对象
     */
    async loadSession({ commit }) {
      // 调用 API 获取当前用户信息
      const session = await getMe();
      // 更新会话状态
      commit("SET_SESSION", session);
      // 返回会话信息供调用方使用
      return session;
    },
    /**
     * 退出登录
     * 
     * 调用登出 API 清除服务端会话，然后重置本地状态。
     * 
     * @param {Object} context - 上下文对象，包含 commit、state 等
     * @returns {Promise<void>}
     */
    async logout({ commit }) {
      // 调用登出 API
      await logout();
      // 重置会话状态为未认证
      commit("SET_SESSION", { authenticated: false });
    }
  }
});
